import { useEffect, useMemo, useState } from "react";
import { SaveRealisationToProfile } from "@/components/app/realisations/SaveRealisationToProfile";
import { ProfileNudge } from "@/components/app/profile/ProfileNudge";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Lightbulb, History, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import RealisationJourney from "@/components/app/RealisationJourney";
import RealisationEffects from "@/components/app/RealisationEffects";
import RealisationRippleSimulator from "@/components/app/RealisationRippleSimulator";
import CurrentRead from "@/components/app/CurrentRead";
import { ProximitySummary } from "@/pages/beta/ProximityPreferenceForm";

const IMPORTANCE = [
  ["unknown", "Not set"],
  ["nice_to_have", "Nice to have"],
  ["important", "Important"],
  ["very_important", "Very important"],
  ["deal_breaker", "Deal-breaker"],
];

const CERTAINTY = [
  ["unknown", "Not set"],
  ["exploring", "Exploring"],
  ["leaning", "Probably true"],
  ["pretty_sure", "Pretty sure"],
  ["certain", "Certain"],
];

const DOMAIN_LABELS = {
  employability: "Career",
  city: "City",
  prestige: "Reputation",
  financial: "Cost & value",
  lifestyle: "Lifestyle",
  country: "Country",
  course: "Course",
  subject: "Subject",
  university: "University",
  academic: "Academic",
  teaching: "Teaching",
  mobility: "Mobility",
  career: "Career",
  other: "Other",
};

function latestWithdrawnRealisations(historyRows) {
  const byCode = new Map();
  for (const row of historyRows || []) {
    const code = row.statement?.canonical_code;
    if (!code) continue;
    const time =
      row.last_confirmed_at ||
      row.confirmation?.confirmed_at ||
      row.first_seen_at ||
      "";
    const existing = byCode.get(code);
    const existingTime =
      existing?.last_confirmed_at ||
      existing?.confirmation?.confirmed_at ||
      existing?.first_seen_at ||
      "";
    if (!existing || time > existingTime) byCode.set(code, row);
  }
  return Array.from(byCode.values())
    .filter((row) => row.state === "withdrawn")
    .sort((a, b) => {
      const at = a.withdrawn_at || a.last_confirmed_at || "";
      const bt = b.withdrawn_at || b.last_confirmed_at || "";
      return bt.localeCompare(at);
    });
}

export default function MyRealisations() {
  const { user, loading: authLoading, login } = useAuth();
  const [rows, setRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [activeResponse, historyResponse] = await Promise.all([
        api.get("/realisations"),
        api.get("/realisations/history"),
      ]);
      setRows(activeResponse.data?.realisations || []);
      setHistoryRows(historyResponse.data?.realisations || []);
    } catch {
      toast.error("We couldn't load your Realisations just now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    else {
      setRows([]);
      setHistoryRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading) return null;

  const pastRows = latestWithdrawnRealisations(historyRows);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="label-uppercase text-[#4C1D95]">My Realisations</div>
        <h1 className="font-editorial text-4xl mt-3">
          The things you've learned about yourself.
        </h1>
        <p className="mt-3 text-stone-600 max-w-xl mx-auto">
          Sign in to see the Realisations you've confirmed while exploring your choices.
        </p>
        <Button
          type="button"
          onClick={login}
          data-testid="realisations-sign-in"
          className="mt-7 bg-[#002147] hover:bg-[#001A38] text-white"
        >
          Sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
      <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
        <Lightbulb className="h-3.5 w-3.5" />
        My Realisations
      </div>
      <h1
        data-testid="realisations-heading"
        className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight"
      >
        What your choices are telling us about you
      </h1>
      <p className="mt-3 text-stone-600 max-w-2xl" data-testid="realisations-subcopy">
        This is WUA's current read from the choices and information you've given us.
        It will change as your thinking changes.
      </p>

      <CurrentRead user={user} />
      <ProfileNudge surface="realisations" className="mt-6"
        priorities={["budget", "goals", "preferences"]}
        lead="WUA's readings here are guesses from your choices: confirm or correct them, and nothing goes into your profile until you do. To sharpen them," />
      {user && <div className="mt-6"><ProximitySummary /></div>}

      <Accordion
        testId="confirmed-accordion"
        title={"Things you've confirmed · " + (loading ? "…" : rows.length)}
        sectionTestId="confirmed-realisations-section"
      >
        <div className="flex items-start gap-2 text-xs text-stone-500">
          <History className="h-3.5 w-3.5 mt-0.5 shrink-0 text-stone-400" />
          <span>You stay in control: change how strongly you feel, or withdraw something that no longer fits. WUA keeps previous versions in your private history.</span>
        </div>
        {!loading && rows.length === 0 && (
          <Card className="mt-4 border-dashed border-stone-300 bg-[#FDFBF7] p-5 text-center" data-testid="realisations-empty">
            <h2 className="font-editorial text-lg">
              {pastRows.length ? "Nothing confirmed right now." : "Nothing confirmed yet."}
            </h2>
            <p className="mt-2 text-sm text-stone-600 max-w-lg mx-auto">
              {pastRows.length
                ? "Your earlier Realisations are still safely kept in your history below."
                : "When you tell WUA a reading sounds like you, it's kept here as something you've confirmed."}
            </p>
            <Link to="/shortlist" className="inline-flex mt-5 text-sm font-semibold text-[#4C1D95] hover:underline">
              Continue exploring your shortlist
            </Link>
          </Card>
        )}
        <div className="mt-4 space-y-3">
          {rows.map((row) => (
            <RealisationCard key={row.realisation_id} row={row} onChanged={load} />
          ))}
        </div>
      </Accordion>

      {!loading && pastRows.length > 0 && (
        <Accordion
          testId="history-accordion"
          title={"History · " + pastRows.length}
          sectionTestId="past-realisations-section"
          secondary
        >
          <p className="text-sm text-stone-600 max-w-2xl">
            Things that used to feel true. Withdrawing a Realisation never erases it: you can still see how
            you reached it and how your thinking changed.
          </p>
          <div className="mt-4 space-y-4">
            {pastRows.map((row) => (
              <PastRealisationCard key={row.realisation_id} row={row} />
            ))}
          </div>
        </Accordion>
      )}
    </div>
  );
}

function RealisationCard({ row, onChanged }) {
  const [importance, setImportance] = useState(row.importance || "unknown");
  const [certainty, setCertainty] = useState(row.certainty || "unknown");
  const [saving, setSaving] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  useEffect(() => {
    setImportance(row.importance || "unknown");
    setCertainty(row.certainty || "unknown");
  }, [row]);

  const dirty = useMemo(
    () =>
      importance !== (row.importance || "unknown") ||
      certainty !== (row.certainty || "unknown"),
    [importance, certainty, row]
  );

  const evidenceCount = row.provenance?.source_event_ids?.length || 0;
  const confirmedAt = row.confirmation?.confirmed_at || row.last_confirmed_at;
  const domainLabel = DOMAIN_LABELS[row.domain] || "Realisation";

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await api.patch("/realisations/" + row.realisation_id, {
        importance,
        certainty,
      });
      toast.success("Realisation updated — your previous version is still in history.");
      await onChanged?.();
    } catch {
      toast.error("We couldn't update that Realisation just now.");
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/realisations/" + row.realisation_id + "/withdraw");
      toast.success("Realisation withdrawn. Its history is preserved.");
      await onChanged?.();
    } catch {
      toast.error("We couldn't withdraw that Realisation just now.");
    } finally {
      setSaving(false);
      setConfirmWithdraw(false);
    }
  };

  return (
    <Card
      className="border-stone-200 bg-white p-4 sm:p-5"
      data-testid={"realisation-card-" + row.realisation_id}
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-underground-sc tracking-[0.14em] text-[#4C1D95]">
            {domainLabel}
          </div>
          <h3 className="font-editorial text-lg mt-1 text-stone-900">
            {row.statement?.student_text}
          </h3>
          <p className="mt-2 text-sm text-stone-500">
            {evidenceCount > 1
              ? "You confirmed this after WUA noticed the same pattern across " + evidenceCount + " choices."
              : "You confirmed this as part of your decision journey."}
            {confirmedAt
              ? " Confirmed " + new Date(confirmedAt).toLocaleDateString() + "."
              : ""}
          </p>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-semibold text-stone-700 flex items-center gap-1.5">
            <SlidersHorizontal className="h-3.5 w-3.5 text-stone-400" />
            How important is this?
          </span>
          <select
            value={importance}
            onChange={(e) => setImportance(e.target.value)}
            data-testid={"realisation-importance-" + row.realisation_id}
            className="mt-1.5 w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-900 outline-none focus:border-[#4C1D95]"
          >
            {IMPORTANCE.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-semibold text-stone-700">
            How sure are you?
          </span>
          <select
            value={certainty}
            onChange={(e) => setCertainty(e.target.value)}
            data-testid={"realisation-certainty-" + row.realisation_id}
            className="mt-1.5 w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-900 outline-none focus:border-[#4C1D95]"
          >
            {CERTAINTY.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid={"realisation-save-" + row.realisation_id}
          className="bg-[#002147] hover:bg-[#001A38] text-white"
        >
          Save changes
        </Button>

        {!confirmWithdraw ? (
          <button
            type="button"
            onClick={() => setConfirmWithdraw(true)}
            disabled={saving}
            data-testid={"realisation-withdraw-start-" + row.realisation_id}
            className="text-sm text-stone-500 hover:text-rose-700 disabled:opacity-50"
          >
            This is no longer true
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-stone-600">Withdraw this Realisation?</span>
            <button
              type="button"
              onClick={withdraw}
              disabled={saving}
              data-testid={"realisation-withdraw-confirm-" + row.realisation_id}
              className="font-semibold text-rose-700 hover:underline disabled:opacity-50"
            >
              Yes, withdraw it
            </button>
            <button
              type="button"
              onClick={() => setConfirmWithdraw(false)}
              disabled={saving}
              className="text-stone-500 hover:underline disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <SaveRealisationToProfile realisationId={row.realisation_id} importance={row.importance} disabled={dirty || saving} />
      {row.statement?.canonical_code === "pref_city_matters" && (
        <>
          <RealisationRippleSimulator
            realisationId={row.realisation_id}
            currentImportance={row.importance || "unknown"}
            disabled={dirty || saving}
            onChanged={onChanged}
          />
          <RealisationEffects realisationId={row.realisation_id} />
        </>
      )}
      <RealisationJourney realisationId={row.realisation_id} />
    </Card>
  );
}

function PastRealisationCard({ row }) {
  const withdrawnAt = row.withdrawn_at
    ? new Date(row.withdrawn_at).toLocaleDateString()
    : null;
  const domainLabel = DOMAIN_LABELS[row.domain] || "Realisation";

  return (
    <Card
      className="border-stone-200 bg-stone-50/60 p-5 sm:p-6"
      data-testid={"past-realisation-card-" + row.realisation_id}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] font-underground-sc tracking-[0.14em] text-stone-500">
          {domainLabel}
        </div>
        <span className="rounded-full bg-stone-200 px-2 py-0.5 text-[10px] font-semibold text-stone-600">
          Withdrawn
        </span>
      </div>
      <h3 className="font-editorial text-2xl mt-1 text-stone-800">
        {row.statement?.student_text}
      </h3>
      <p className="mt-2 text-sm text-stone-500">
        {withdrawnAt
          ? "You said this was no longer true on " + withdrawnAt + "."
          : "You said this was no longer true."}
      </p>

      <RealisationJourney realisationId={row.realisation_id} />
    </Card>
  );
}

function Accordion({ testId, title, sectionTestId, secondary = false, children }) {
  const [open, setOpen] = useState(false);
  return (
    <section className={secondary ? "mt-6" : "mt-12"} data-testid={sectionTestId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={testId}
        className={
          "flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors " +
          (secondary
            ? "border-stone-200 bg-transparent text-stone-500 hover:text-stone-800"
            : "border-stone-300 bg-white text-stone-800 hover:border-[#4C1D95]")
        }
      >
        <span className={secondary ? "text-sm" : "font-editorial text-xl"}>{title}</span>
        {open ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      </button>
      {open && <div className="mt-4" data-testid={testId + "-content"}>{children}</div>}
    </section>
  );
}
